import React from 'react';
import { Select } from 'antd';
import {
  CloseOutlined, DownOutlined, FilterOutlined, UpOutlined,
} from '@ant-design/icons';
import { COUPON_OPTIONS, REDEMPTION_OPTIONS, RATING_OPTIONS } from './portfolioFilterOptions';
import './PortfolioRefineFilters.css';

function optionLabel(options, value) {
  return options.find((o) => o.value === value)?.label || value;
}

export default function PortfolioRefineFilters({
  expanded,
  onToggleExpanded,
  redemptionFilter,
  onRedemptionChange,
  couponFilter,
  onCouponChange,
  ratingFilter,
  onRatingChange,
  showRatingFilter,
  activeCount,
  hasSearched,
  filterLoading,
  loading,
  totalItems,
  currentPage,
  pageSize,
  onApply,
  onClearAll,
  onRemoveChip,
}) {
  const showChips = hasSearched && activeCount > 0;

  const chips = [];
  if (hasSearched && redemptionFilter) {
    chips.push({
      key: 'redemption',
      label: `Redemption: ${optionLabel(REDEMPTION_OPTIONS, redemptionFilter)}`,
    });
  }
  if (hasSearched && couponFilter) {
    chips.push({
      key: 'coupon',
      label: `Coupon: ${optionLabel(COUPON_OPTIONS, couponFilter)}`,
    });
  }
  if (hasSearched && showRatingFilter && ratingFilter) {
    chips.push({
      key: 'rating',
      label: `Rating: ${ratingFilter}`,
    });
  }

  return (
    <div className="hr-refine">
      <div className="hr-results-bar">
        <div className="hr-results-bar__left">
          <span className="hr-results-bar__label">Bond results</span>
          <span className="hr-results-count">
            {loading ? (
              'Loading…'
            ) : (
              <>
                <strong>{totalItems.toLocaleString()}</strong>
                {` ${totalItems === 1 ? 'bond' : 'bonds'}`}
                {totalItems > pageSize && (
                  <span className="hr-results-count__page">
                    {` · showing ${((currentPage - 1) * pageSize) + 1}–${Math.min(currentPage * pageSize, totalItems)}`}
                  </span>
                )}
              </>
            )}
          </span>
        </div>

        <button
          type="button"
          className={`hr-refine-toggle${expanded ? ' is-open' : ''}${activeCount > 0 ? ' has-active' : ''}`}
          onClick={onToggleExpanded}
          aria-expanded={expanded}
        >
          <FilterOutlined aria-hidden />
          <span>Refine</span>
          {activeCount > 0 && (
            <span className="hr-refine-toggle__count">{activeCount}</span>
          )}
          {expanded ? <UpOutlined aria-hidden /> : <DownOutlined aria-hidden />}
        </button>
      </div>

      {showChips && (
        <div className="hr-filter-chips" aria-label="Active filters">
          {chips.map((chip) => (
            <span key={chip.key} className="hr-filter-chip">
              {chip.label}
              <button
                type="button"
                className="hr-filter-chip__remove"
                onClick={() => onRemoveChip(chip.key)}
                aria-label={`Remove ${chip.label}`}
              >
                <CloseOutlined aria-hidden />
              </button>
            </span>
          ))}
          <button type="button" className="hr-filter-chips__clear" onClick={onClearAll}>
            Clear all
          </button>
        </div>
      )}

      {expanded && (
        <div className="hr-refine-panel">
          <p className="hr-refine-panel__hint">Optional — narrow bonds by redemption, coupon, or rating.</p>
          <div
            className="hr-refine-panel__grid"
            style={{ '--filter-columns': 2 + (showRatingFilter ? 1 : 0) }}
          >
            <div className="hr-filter-group">
              <label className="hr-filter-label" htmlFor="hr-refine-redemption">Redemption</label>
              <Select
                id="hr-refine-redemption"
                placeholder="Any period"
                value={redemptionFilter}
                onChange={onRedemptionChange}
                options={REDEMPTION_OPTIONS}
                allowClear
                style={{ width: '100%' }}
                className="hr-ant-select"
                dropdownStyle={{ zIndex: 9999 }}
              />
            </div>

            <div className="hr-filter-group">
              <label className="hr-filter-label" htmlFor="hr-refine-coupon">Coupon rate</label>
              <Select
                id="hr-refine-coupon"
                placeholder="Any rate"
                value={couponFilter}
                onChange={onCouponChange}
                options={COUPON_OPTIONS}
                allowClear
                style={{ width: '100%' }}
                className="hr-ant-select"
                dropdownStyle={{ zIndex: 9999 }}
              />
            </div>

            {showRatingFilter && (
              <div className="hr-filter-group">
                <label className="hr-filter-label" htmlFor="hr-refine-rating">Rating</label>
                <Select
                  id="hr-refine-rating"
                  placeholder="Any rating"
                  value={ratingFilter}
                  onChange={onRatingChange}
                  options={RATING_OPTIONS}
                  allowClear
                  style={{ width: '100%' }}
                  className="hr-ant-select"
                  dropdownStyle={{ zIndex: 9999 }}
                />
              </div>
            )}
          </div>

          <div className="hr-refine-panel__actions">
            <button
              type="button"
              className="hr-btn-clear"
              onClick={onClearAll}
              disabled={!activeCount && !hasSearched}
            >
              Reset
            </button>
            <button
              type="button"
              className="hr-btn-apply"
              onClick={onApply}
              disabled={filterLoading || activeCount === 0}
            >
              {filterLoading ? 'Applying…' : 'Apply'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
